import "../../components/EventUserDashboard.css";

import React, { useEffect, useMemo, useState } from "react";
import { Select } from "@mantine/core";

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface AssignedDrinkPersonDto {
    personId: number;
    fullName: string;
    functionName?: string;
    bar1Assigned: number;
    bar2Assigned: number;
    bar3Assigned: number;
    bar1Used: number;
    bar2Used: number;
    bar3Used: number;
    totalAssigned: number;
    totalUsed: number;
    totalRemaining: number;
    validDate?: string | null;
}

enum Bar {
    BAR1 = 1,
    BAR2 = 2,
    BAR3 = 3
}

const getBarName = (bar: number) => {
    switch (bar) {
        case Bar.BAR1:
            return "VIP Lounge";
        case Bar.BAR2:
            return "Hygge Lounge";
        case Bar.BAR3:
            return "Jail House Bar";
        default:
            return `Bar ${bar}`;
    }
};

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.results)) return result.results;
    if (Array.isArray(result?.value)) return result.value;
    if (Array.isArray(result?.events)) return result.events;
    return [];
};

const toNumber = (value: any, fallback = 0): number => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

const normalizeEvent = (x: any): EventDto => {
    return {
        eventID: toNumber(
            x?.eventID ??
            x?.EventID ??
            x?.eventId ??
            x?.EventId ??
            x?.id ??
            x?.Id
        ),
        eventName:
            x?.eventName ??
            x?.EventName ??
            x?.name ??
            x?.Name ??
            ""
    };
};

const getTodayString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const formatDate = (value?: string | null) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString("en-GB");
};

const AdminDrinkAssignedPersons: React.FC = () => {
    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [rows, setRows] = useState<AssignedDrinkPersonDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [selectedName, setSelectedName] = useState<string | null>(null);
    const [selectedFunction, setSelectedFunction] = useState<string | null>(null);
    const [validDate, setValidDate] = useState<string>(getTodayString());

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch("/api/events");
                if (!res.ok) {
                    console.error("Could not load events:", res.status);
                    return;
                }

                const data = await res.json();

                const mapped = toArray<any>(data)
                    .map(normalizeEvent)
                    .filter((e) => e.eventID > 0);

                setEvents(mapped);
            } catch (err) {
                console.error("Error loading events:", err);
            }
        };

        loadEvents();
    }, []);

    const fetchAssignedPersons = async (currentEventId: number, currentValidDate: string) => {
        if (!currentEventId || !currentValidDate) {
            setRows([]);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const res = await fetch(
                `/api/drinks/assigned-persons?eventId=${currentEventId}&validDate=${encodeURIComponent(currentValidDate)}`
            );

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Could not fetch assigned drinks.");
            }

            const data = await res.json();

            const mapped = toArray<any>(data).map(
                (x): AssignedDrinkPersonDto => ({
                    personId: toNumber(x.personId ?? x.PersonId),
                    fullName:
                        x.fullName ??
                        x.FullName ??
                        `${x.firstName ?? x.FirstName ?? ""} ${x.lastName ?? x.LastName ?? ""}`.trim(),
                    functionName: x.functionName ?? x.FunctionName ?? "",
                    bar1Assigned: toNumber(x.bar1Assigned ?? x.Bar1Assigned),
                    bar2Assigned: toNumber(x.bar2Assigned ?? x.Bar2Assigned),
                    bar3Assigned: toNumber(x.bar3Assigned ?? x.Bar3Assigned),
                    bar1Used: toNumber(x.bar1Used ?? x.Bar1Used),
                    bar2Used: toNumber(x.bar2Used ?? x.Bar2Used),
                    bar3Used: toNumber(x.bar3Used ?? x.Bar3Used),
                    totalAssigned: toNumber(x.totalAssigned ?? x.TotalAssigned),
                    totalUsed: toNumber(x.totalUsed ?? x.TotalUsed),
                    totalRemaining: toNumber(x.totalRemaining ?? x.TotalRemaining),
                    validDate: x.validDate ?? x.ValidDate ?? null
                })
            );

            setRows(mapped);
        } catch (err: any) {
            console.error("Error loading assigned drinks:", err);
            setRows([]);
            setError(err?.message || "Could not fetch assigned drinks.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSelectedName(null);
        setSelectedFunction(null);

        if (eventId > 0 && validDate) {
            fetchAssignedPersons(eventId, validDate);
        } else {
            setRows([]);
        }
    }, [eventId, validDate]);

    const availableNames = useMemo(() => {
        return Array.from(
            new Set(
                rows
                    .map((x) => x.fullName || "")
                    .filter((x) => !!x.trim())
            )
        ).sort((a, b) => a.localeCompare(b, "da"));
    }, [rows]);

    const availableFunctions = useMemo(() => {
        return Array.from(
            new Set(
                rows
                    .map((x) => x.functionName || "")
                    .filter((x) => !!x.trim())
            )
        ).sort((a, b) => a.localeCompare(b, "da"));
    }, [rows]);

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            const matchesName =
                !selectedName ||
                row.fullName === selectedName;

            const matchesFunction =
                !selectedFunction ||
                (row.functionName || "") === selectedFunction;

            return matchesName && matchesFunction;
        });
    }, [rows, selectedName, selectedFunction]);

    const totals = useMemo(() => {
        return filteredRows.reduce(
            (acc, row) => {
                acc.bar1Assigned += row.bar1Assigned;
                acc.bar2Assigned += row.bar2Assigned;
                acc.bar3Assigned += row.bar3Assigned;
                acc.bar1Used += row.bar1Used;
                acc.bar2Used += row.bar2Used;
                acc.bar3Used += row.bar3Used;
                acc.totalAssigned += row.totalAssigned;
                acc.totalUsed += row.totalUsed;
                acc.totalRemaining += row.totalRemaining;
                return acc;
            },
            {
                bar1Assigned: 0,
                bar2Assigned: 0,
                bar3Assigned: 0,
                bar1Used: 0,
                bar2Used: 0,
                bar3Used: 0,
                totalAssigned: 0,
                totalUsed: 0,
                totalRemaining: 0
            }
        );
    }, [filteredRows]);

    return (
        <div className="dashContainer">
            <div className="wrapper">
                <div className="top">
                    <div className="name">Assigned Drinks Overview</div>
                    <div className="meta">
                        See all persons with assigned drinks, personal and by function
                    </div>
                </div>

                <div className="content">
                    {error && (
                        <div className="badgeRed" style={{ padding: 8, marginBottom: 16 }}>
                            {error}
                        </div>
                    )}

                    <div className="sectionTitle">Filters</div>

                    <div className="statsGrid3" style={{ marginTop: 12 }}>
                        <div className="stat" style={{ padding: 16 }}>
                            <label>Event</label>
                            <Select
                                placeholder="Select event"
                                value={eventId ? String(eventId) : null}
                                onChange={(val) => setEventId(val ? Number(val) : 0)}
                                data={events.map((e) => ({
                                    value: String(e.eventID),
                                    label: e.eventName || `Event ${e.eventID}`
                                }))}
                                nothingFoundMessage="No events found"
                            />
                        </div>

                        <div className="stat" style={{ padding: 16 }}>
                            <label>Select name</label>
                            <Select
                                searchable
                                clearable
                                placeholder="Select name"
                                value={selectedName}
                                onChange={setSelectedName}
                                data={availableNames.map((name) => ({
                                    value: name,
                                    label: name
                                }))}
                                nothingFoundMessage="No names found"
                            />
                        </div>

                        <div className="stat" style={{ padding: 16 }}>
                            <label>Filter by function</label>
                            <Select
                                searchable
                                clearable
                                placeholder="Select function"
                                value={selectedFunction}
                                onChange={setSelectedFunction}
                                data={availableFunctions.map((fn) => ({
                                    value: fn,
                                    label: fn
                                }))}
                                nothingFoundMessage="No functions found"
                            />
                        </div>
                    </div>

                    <div className="statsGrid3" style={{ marginTop: 12 }}>
                        <div className="stat" style={{ padding: 16 }}>
                            <label>Valid date</label>
                            <input
                                type="date"
                                value={validDate}
                                onChange={(e) => setValidDate(e.target.value)}
                            />
                        </div>

                        <div className="stat" style={{ padding: 16, display: "flex", alignItems: "end" }}>
                            <div>
                                Selected date: <strong>{formatDate(validDate)}</strong>
                            </div>
                        </div>
                    </div>

                    {loading && (
                        <div style={{ marginTop: 20 }}>
                            Loading assigned drinks...
                        </div>
                    )}

                    {!loading && filteredRows.length > 0 && (
                        <>
                            <div className="sectionTitle" style={{ marginTop: 30 }}>
                                Result
                            </div>

                            <div className="stat" style={{ padding: 0, marginTop: 12, overflowX: "auto" }}>
                                <table
                                    style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                        minWidth: 1100
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={thLeft}>Name</th>
                                            <th style={thLeft}>Function</th>
                                            <th style={th}>{getBarName(Bar.BAR1)}</th>
                                            <th style={th}>{getBarName(Bar.BAR2)}</th>
                                            <th style={th}>{getBarName(Bar.BAR3)}</th>
                                            <th style={th}>Used</th>
                                            <th style={th}>Assigned</th>
                                            <th style={th}>Remaining</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRows.map((row) => (
                                            <tr key={`${row.personId}-${row.functionName || ""}`}>
                                                <td style={tdLeft}>{row.fullName}</td>
                                                <td style={tdLeft}>{row.functionName || "-"}</td>
                                                <td style={td}>
                                                    {row.bar1Assigned}
                                                    {row.bar1Used > 0 && (
                                                        <div style={usedTextStyle}>used: {row.bar1Used}</div>
                                                    )}
                                                </td>
                                                <td style={td}>
                                                    {row.bar2Assigned}
                                                    {row.bar2Used > 0 && (
                                                        <div style={usedTextStyle}>used: {row.bar2Used}</div>
                                                    )}
                                                </td>
                                                <td style={td}>
                                                    {row.bar3Assigned}
                                                    {row.bar3Used > 0 && (
                                                        <div style={usedTextStyle}>used: {row.bar3Used}</div>
                                                    )}
                                                </td>
                                                <td style={td}>{row.totalUsed}</td>
                                                <td style={{ ...td, fontWeight: 800 }}>{row.totalAssigned}</td>
                                                <td style={{ ...td, fontWeight: 800 }}>{row.totalRemaining}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ fontWeight: 800 }}>
                                            <td style={tdLeft}>TOTAL</td>
                                            <td style={tdLeft}></td>
                                            <td style={td}>{totals.bar1Assigned}</td>
                                            <td style={td}>{totals.bar2Assigned}</td>
                                            <td style={td}>{totals.bar3Assigned}</td>
                                            <td style={td}>{totals.totalUsed}</td>
                                            <td style={td}>{totals.totalAssigned}</td>
                                            <td style={td}>{totals.totalRemaining}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </>
                    )}

                    {!loading && eventId > 0 && filteredRows.length === 0 && (
                        <div style={{ marginTop: 20 }}>
                            No assigned drinks found.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const th = {
    padding: "12px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    textAlign: "center" as const
};

const thLeft = {
    ...th,
    textAlign: "left" as const
};

const td = {
    padding: "10px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    textAlign: "center" as const,
    verticalAlign: "top" as const
};

const tdLeft = {
    ...td,
    textAlign: "left" as const
};

const usedTextStyle = {
    marginTop: 4,
    fontSize: 12,
    opacity: 0.7
};

export default AdminDrinkAssignedPersons;